// ================= THE FIRST-TRIGGER COACH =================
//
// TEACH A MECHANIC THE FIRST TIME IT ACTUALLY HAPPENS, then never again. The brief that commissioned
// this (sparring-cards-mechanics-visibility-implementation-brief.pdf) is explicit about the
// alternative and why it is worse:
//
//     "Do not add a large rules wall to the landing page. Add a short setup statement and then teach
//      each mechanic the first time it actually triggers."
//
// A rules wall is read by nobody and is read at the one moment the reader has no fight to attach it
// to. A line that appears the first time YOUR guard absorbs something, attached to the exchange that
// just happened, is the same sentence delivered when it means something.
//
// PER MECHANIC, NOT ONE TUTORIAL FLAG, which the brief also states outright: "Store tutorial
// completion per mechanic, not as one all-or-nothing tutorial flag." The four Defense Types are not
// learned together -- a player can meet Absorb in their first bout and Dodge in their fortieth, and
// a single `tutorialDone` flag set by the first would silently swallow the other three.
//
// PER RULES VERSION TOO. The key carries the ruleset id, so a change to what Absorb DOES re-teaches
// it rather than leaving a player confident about a rule that has moved underneath them. That is
// the same reasoning js/model-weights.js records for the trained agent: nothing else in the repo
// remembers which rules a stored judgement was made under.
//
// CLAIMED AT RENDER, NOT AT RESOLUTION. `claim` is called by the beat renderer while it is building
// the aftermath row, and it both answers and consumes in one call. That is deliberate: a mechanic
// that fires in an exchange the player never watched (they left the bout, or the beat was never
// reached) has not been taught, and a separate "mark seen" step would mark it anyway. The cost is
// that `claim` is not idempotent within a render -- see the IN-FLIGHT note below, which is what
// stops one exchange's re-render from spending four different mechanics' first impressions.
(function () {
  'use strict';

  // Bumped by the ruleset, not by this file: it is the version whose behaviour was taught.
  //
  // IT READS ENGINE.RULESET, NOT CARDS_V4.rulesetVersion, and the difference is the whole
  // property. CARDS_V4.rulesetVersion names the card SCHEMA -- it has read
  // 'contact-and-commitment.v4-speed-playtest' since the Speed Edition landed and does not move
  // when a rule does. ENGINE.RULESET is the date stamp that legality, resolution and card
  // numbers actually bump. Keyed on the schema, the re-teach this file was written for had
  // NEVER FIRED once: the deck cycle of 2026-08-24 changed what a hand is and every player who
  // had already met Fatigue kept a lesson taught under the older rule.
  //
  // Same defect as the trained agent's provenance header (fixed 2026-08-26, tools/train-run.js)
  // and for the same reason -- two version strings, one of which is not a version of anything
  // that changes. The schema string stays as the fallback so a build with no engine loaded
  // still keys on something stable rather than on 'unknown'.
  function rulesVersion() {
    const e = window.ENGINE;
    if (e && e.RULESET) return e.RULESET;
    const v4 = window.CARDS_V4;
    return (v4 && v4.rulesetVersion) || 'unknown';
  }

  const PREFIX = 'sc.coach.';

  // Every access guarded. localStorage THROWS rather than returning null in a Safari private
  // window and in some webviews -- the same posture as js/sfx.js and js/ui-home.js. With no storage
  // the coach simply teaches every time, which is a degraded experience rather than a broken one,
  // and is strictly better than teaching never.
  function read(key) {
    try { return window.localStorage.getItem(PREFIX + key); } catch (e) { return null; }
  }
  function write(key, value) {
    try { window.localStorage.setItem(PREFIX + key, value); } catch (e) { /* not persisted */ }
  }

  function storageKey(mechanic) {
    return mechanic + '.' + rulesVersion();
  }

  // WHAT IS BEING TAUGHT RIGHT NOW, cleared per render pass by beginRender(). A resolution beat is
  // re-rendered on every reveal step and on every window resize, and without this the SECOND render
  // of the same beat would find the mechanic already stored and quietly drop the coaching line the
  // first render had put there -- the row would visibly lose its explanation while the player was
  // reading it. Holding the claim for the life of a render pass makes the answer stable.
  let inFlight = {};
  let taughtThisRender = 0;

  function beginRender() { inFlight = {}; taughtThisRender = 0; }

  // ONE NEW MECHANIC PER RENDER, AND THE LIMIT IS BOTH PEDAGOGY AND GEOMETRY.
  //
  // A player's first exchange can trigger four mechanics at once -- a Dodge, an Impact/Recovery, an
  // Absorb and a Chain all resolved in the same beat -- and the first version taught all four
  // simultaneously. Two things were wrong with that. Four unfamiliar rules stacked in one panel is
  // the rules wall the brief exists to avoid, arriving one screen later than the landing page it
  // was banned from. And measured, it made the beat panel 384px tall at 360x800, against a budget
  // that says the most-repeated screen in the game must fit one viewport -- so the teaching pushed
  // the thing being taught off the bottom of the screen.
  //
  // The mechanics not taught here are not marked taught: they keep their turn and get it the next
  // time they fire, which for anything common is the next exchange or two.
  const MAX_TAUGHT_PER_RENDER = 1;

  // True at most once per mechanic per device per ruleset. Returns true for the render that should
  // carry the teaching line, and false forever after.
  function claim(mechanic) {
    if (!mechanic) return false;
    if (Object.prototype.hasOwnProperty.call(inFlight, mechanic)) return inFlight[mechanic];
    const key = storageKey(mechanic);
    if (read(key)) { inFlight[mechanic] = false; return false; }
    if (taughtThisRender >= MAX_TAUGHT_PER_RENDER) {
      // Deliberately NOT recorded in `inFlight`: this mechanic has not been answered for this
      // render so much as deferred, and leaving it unrecorded is what lets a re-render of the same
      // beat -- which starts a fresh pass -- still be able to teach it if the slot is free.
      return false;
    }
    write(key, '1');
    taughtThisRender++;
    inFlight[mechanic] = true;
    if (window.MechanicsTelemetry && window.MechanicsTelemetry.note) {
      window.MechanicsTelemetry.note('coachShown', mechanic);
    }
    return true;
  }

  // Whether a mechanic has been taught, without teaching it. For a caller that wants to know
  // without spending the one chance -- the telemetry readout uses this.
  function taught(mechanic) { return !!read(storageKey(mechanic)); }

  // Exposed so a player can meet the coaching again, and so a test can start from a known state.
  // There is deliberately no UI for this yet: it is a developer and test affordance, and a "reset
  // tutorials" button is a settings-screen decision rather than a mechanics one.
  function reset() {
    try {
      const store = window.localStorage;
      const kill = [];
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (k && k.indexOf(PREFIX) === 0) kill.push(k);
      }
      kill.forEach(k => store.removeItem(k));
    } catch (e) { /* nothing to clear */ }
    inFlight = {};
  }

  window.MechanicsCoach = { claim, taught, reset, beginRender, rulesVersion, PREFIX };
})();
